import { useMemo } from 'react'
import { Link, useParams } from 'react-router'
import { ArrowLeft, Pencil, Calendar, CreditCard, Landmark, Repeat } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { formatAmount } from '@/lib/utils'
import { UNCATEGORIZED_LABEL } from '@/lib/constants'
import {
  usePaymentMethod,
  usePaymentMethods,
  usePaymentMethodImageUrls,
  useRecurring,
  useUsdToJpyRate,
} from '@/lib/hooks'
import { convertUsdToJpy } from '@/lib/exchange-rate'
import type { PaymentMethod, RecurringTransaction } from '@/lib/hooks'
import { PaymentMethodCard } from '@/components/payment-method/payment-method-card'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'

function computeTotals(active: RecurringTransaction[]) {
  let monthlyJpy = 0
  let monthlyUsd = 0
  let yearlyJpy = 0
  let yearlyUsd = 0
  for (const r of active) {
    // monthly average
    if (r.frequency === 'monthly') {
      if (r.currency === 'USD') monthlyUsd += r.amount
      else monthlyJpy += r.amount
    } else {
      const months = r.frequency_months?.length ?? 0
      if (months > 0) {
        const perMonth = (r.amount * months) / 12
        if (r.currency === 'USD') monthlyUsd += perMonth
        else monthlyJpy += perMonth
      }
    }
    // yearly
    const annual = r.frequency === 'yearly'
      ? r.amount * (r.frequency_months?.length ?? 0)
      : r.amount * 12
    if (r.currency === 'USD') yearlyUsd += annual
    else yearlyJpy += annual
  }
  return {
    monthly: { jpy: Math.round(monthlyJpy), usd: Math.round(monthlyUsd * 100) / 100 },
    yearly: { jpy: Math.round(yearlyJpy), usd: Math.round(yearlyUsd * 100) / 100 },
  }
}

function formatJpyWithUsd(jpy: number, usd: number, rate?: number | null): React.ReactNode {
  const combined = rate && usd > 0 ? jpy + convertUsdToJpy(usd, rate) : null
  return (
    <>
      {combined != null ? formatAmount(combined) : formatAmount(jpy)}
      {combined == null && usd > 0 && ` + ${formatAmount(usd, 'USD')}`}
    </>
  )
}

function formatDay(day: number | null, endOfMonth: boolean): string {
  if (endOfMonth) return '末日'
  if (day == null) return '未設定'
  return `${day}日`
}

function PaymentScheduleRow({ source, linkedBank }: { source: PaymentMethod; linkedBank: PaymentMethod | null }) {
  if (source.type !== 'credit_card') return null
  return (
    <Card className="py-0">
      <CardContent className="space-y-3 px-4 py-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Calendar className="size-4 text-muted-foreground" />
          引き落としスケジュール
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-xs text-muted-foreground">締め日</p>
            <p className="tabular-nums">{formatDay(source.closing_day, source.closing_day_end_of_month)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">引き落とし日</p>
            <p className="tabular-nums">
              {source.payment_month_offset === 2 ? '翌々月' : '翌月'}
              {' '}
              {formatDay(source.payment_day, source.payment_day_end_of_month)}
            </p>
          </div>
        </div>
        {linkedBank && (
          <div className="border-t pt-3">
            <p className="text-xs text-muted-foreground">引き落とし口座</p>
            <Link
              to={`/settings/payment-methods/${linkedBank.id}`}
              className="mt-0.5 inline-flex items-center gap-1.5 text-sm hover:underline"
            >
              <Landmark className="size-3.5 text-muted-foreground" />
              {linkedBank.name}
              {linkedBank.bank_name && (
                <span className="text-xs text-muted-foreground">({linkedBank.bank_name})</span>
              )}
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function BankInfoRow({ source }: { source: PaymentMethod }) {
  if (source.type !== 'bank_account') return null
  if (!source.bank_name && !source.branch_name) return null
  return (
    <Card className="py-0">
      <CardContent className="space-y-2 px-4 py-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <CreditCard className="size-4 text-muted-foreground" />
          口座情報
        </div>
        <div className="space-y-1 text-sm">
          {source.bank_name && <p>{source.bank_name}{source.branch_name && ` ${source.branch_name}`}</p>}
        </div>
      </CardContent>
    </Card>
  )
}

function RecurringRow({ item }: { item: RecurringTransaction }) {
  return (
    <Link
      to="/recurring"
      className="flex items-center justify-between rounded-lg px-2 py-3 active:bg-accent/80 transition-colors"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{item.name}</span>
          {!item.is_active && (
            <Badge variant="secondary" className="shrink-0 text-xs">停止中</Badge>
          )}
        </div>
        <span className="flex items-center gap-1 text-xs text-muted-foreground truncate">
          {item.parent_category_color ? (
            <span
              className="inline-block size-2 shrink-0 rounded-full"
              style={{ backgroundColor: item.parent_category_color }}
            />
          ) : (
            <span className="inline-block size-2 shrink-0 rounded-full border border-muted-foreground" />
          )}
          {item.category_name ?? UNCATEGORIZED_LABEL}
        </span>
      </div>
      <span className="shrink-0 text-sm font-medium tabular-nums">
        {formatAmount(item.amount, item.currency)}
      </span>
    </Link>
  )
}

export default function PaymentMethodDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { data: source, isLoading } = usePaymentMethod(id)
  const { data: allPaymentMethods = [] } = usePaymentMethods()
  const { data: allRecurring = [] } = useRecurring('all')
  const { data: imageUrlMap = {} } = usePaymentMethodImageUrls(source ? [source] : [])

  const linkedBank = source?.linked_bank_method_id
    ? (allPaymentMethods.find((m) => m.id === source.linked_bank_method_id) ?? null)
    : null
  const { data: usdToJpyRate } = useUsdToJpyRate()

  const linkedActive = useMemo(
    () => allRecurring.filter((r) => r.payment_method_id === id && r.is_active),
    [allRecurring, id],
  )
  const linkedInactive = useMemo(
    () => allRecurring.filter((r) => r.payment_method_id === id && !r.is_active),
    [allRecurring, id],
  )

  const totals = useMemo(() => computeTotals(linkedActive), [linkedActive])
  const monthlyTotal = totals.monthly
  const yearlyTotal = totals.yearly

  const monthlyCombined = useMemo(() => {
    if (!usdToJpyRate || monthlyTotal.usd === 0) return null
    return monthlyTotal.jpy + convertUsdToJpy(monthlyTotal.usd, usdToJpyRate)
  }, [monthlyTotal, usdToJpyRate])

  const yearlyCombined = useMemo(() => {
    if (!usdToJpyRate || yearlyTotal.usd === 0) return null
    return yearlyTotal.jpy + convertUsdToJpy(yearlyTotal.usd, usdToJpyRate)
  }, [yearlyTotal, usdToJpyRate])

  // 銀行口座の場合: この口座を「引き落とし口座」 として指しているクレカ一覧 +
  // 各カードの紐付く recurring を集計してアコーディオン表示する。
  const linkedCards = useMemo(() => {
    if (source?.type !== 'bank_account') return [] as Array<{
      card: PaymentMethod
      activeRecurring: RecurringTransaction[]
      totals: ReturnType<typeof computeTotals>
    }>
    return allPaymentMethods
      .filter((m) => m.type === 'credit_card' && m.linked_bank_method_id === source.id)
      .map((card) => {
        const cardRecurring = allRecurring.filter(
          (r) => r.payment_method_id === card.id && r.is_active,
        )
        return {
          card,
          activeRecurring: cardRecurring,
          totals: computeTotals(cardRecurring),
        }
      })
  }, [source, allPaymentMethods, allRecurring])

  // 「紐づくクレジットカード引き落とし」 セクション全体の月間/年間合計。
  // 各カードの合計の sum を取る (= flatten した recurring を一度に集計しても等価)。
  const linkedCardsTotals = useMemo(() => {
    const allCardRecurring = linkedCards.flatMap((c) => c.activeRecurring)
    return computeTotals(allCardRecurring)
  }, [linkedCards])

  // 銀行口座の「直接の引き落とし」+「カード経由の引き落とし」 の総合計。
  // ユーザーが口座から月にいくら出ていくかを一目で把握できるようにする。
  const grandTotals = useMemo(() => {
    if (source?.type !== 'bank_account' || linkedCards.length === 0) return null
    const allRec = [...linkedActive, ...linkedCards.flatMap((c) => c.activeRecurring)]
    return computeTotals(allRec)
  }, [source, linkedActive, linkedCards])

  if (isLoading) {
    return (
      <div className="mx-auto max-w-lg px-4 py-6">
        <Skeleton className="h-44 w-full rounded-xl" />
      </div>
    )
  }

  if (!source) {
    return (
      <div className="mx-auto max-w-lg px-4 py-6">
        <p className="text-sm text-muted-foreground">支払い方法が見つかりません</p>
      </div>
    )
  }

  const imageUrl = source.image_path ? imageUrlMap[source.image_path] : null

  return (
    <div className="mx-auto max-w-lg px-4 py-6">
      <div className="mb-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Link to="/settings/payment-methods" replace>
            <Button variant="ghost" size="icon">
              <ArrowLeft className="size-5" />
            </Button>
          </Link>
          <h1 className="truncate text-xl font-semibold">{source.name}</h1>
          {source.scope === 'personal' && (
            <Badge variant="outline" className="shrink-0 text-xs">個人</Badge>
          )}
        </div>
        <Button variant="ghost" size="icon" asChild>
          <Link to={`/settings/payment-methods/${source.id}/edit`} aria-label="編集">
            <Pencil className="size-4" />
          </Link>
        </Button>
      </div>

      <div className="flex justify-center py-2">
        <PaymentMethodCard source={source} imageUrl={imageUrl} size="lg" />
      </div>

      <div className="space-y-3 pt-4">
        <PaymentScheduleRow source={source} linkedBank={linkedBank} />
        <BankInfoRow source={source} />

        {grandTotals && (
          <Card className="py-0">
            <CardContent className="space-y-2 px-4 py-4">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Landmark className="size-4 text-muted-foreground" />
                この口座からの引き落とし合計
              </div>
              <div className="rounded-md border bg-muted/40 px-3 py-2.5">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">月間合計</p>
                    <p className="font-medium tabular-nums">
                      {formatJpyWithUsd(grandTotals.monthly.jpy, grandTotals.monthly.usd, usdToJpyRate)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">年間合計</p>
                    <p className="font-medium tabular-nums">
                      {formatJpyWithUsd(grandTotals.yearly.jpy, grandTotals.yearly.usd, usdToJpyRate)}
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground">
                直接の固定支出 + 紐づくクレジットカード経由の引き落としを合算。
              </p>
            </CardContent>
          </Card>
        )}

        <Card className="py-0">
          <CardContent className="space-y-3 px-4 py-4">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Repeat className="size-4 text-muted-foreground" />
              紐付く固定支出
            </div>
            {linkedActive.length === 0 && linkedInactive.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                この支払い方法に紐付く固定支出はありません
              </p>
            ) : (
              <>
                <div className="rounded-md border bg-muted/40 px-3 py-2.5">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">月間合計</p>
                      <p className="font-medium tabular-nums">
                        {monthlyCombined != null
                          ? formatAmount(monthlyCombined)
                          : formatAmount(monthlyTotal.jpy)}
                        {monthlyCombined == null && monthlyTotal.usd > 0 && ` + ${formatAmount(monthlyTotal.usd, 'USD')}`}
                      </p>
                      {monthlyCombined != null && monthlyTotal.usd > 0 && usdToJpyRate && (
                        <p className="text-[10px] text-muted-foreground tabular-nums">
                          {formatAmount(monthlyTotal.usd, 'USD')} @ ¥{usdToJpyRate.toFixed(2)}/$ 含む
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">年間合計</p>
                      <p className="font-medium tabular-nums">
                        {yearlyCombined != null
                          ? formatAmount(yearlyCombined)
                          : formatAmount(yearlyTotal.jpy)}
                        {yearlyCombined == null && yearlyTotal.usd > 0 && ` + ${formatAmount(yearlyTotal.usd, 'USD')}`}
                      </p>
                      {yearlyCombined != null && yearlyTotal.usd > 0 && usdToJpyRate && (
                        <p className="text-[10px] text-muted-foreground tabular-nums">
                          {formatAmount(yearlyTotal.usd, 'USD')} @ ¥{usdToJpyRate.toFixed(2)}/$ 含む
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="divide-y">
                  {linkedActive.map((r) => <RecurringRow key={r.id} item={r} />)}
                  {linkedInactive.length > 0 && (
                    <>
                      <p className="px-2 pt-2 text-xs text-muted-foreground">停止中</p>
                      {linkedInactive.map((r) => <RecurringRow key={r.id} item={r} />)}
                    </>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {source.type === 'bank_account' && linkedCards.length > 0 && (
          <Card className="py-0">
            <CardContent className="space-y-3 px-4 py-4">
              <div className="flex items-center gap-2 text-sm font-medium">
                <CreditCard className="size-4 text-muted-foreground" />
                紐づくクレジットカード引き落とし
              </div>
              <div className="rounded-md border bg-muted/40 px-3 py-2.5">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">月間合計</p>
                    <p className="font-medium tabular-nums">
                      {formatJpyWithUsd(
                        linkedCardsTotals.monthly.jpy,
                        linkedCardsTotals.monthly.usd,
                        usdToJpyRate,
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">年間合計</p>
                    <p className="font-medium tabular-nums">
                      {formatJpyWithUsd(
                        linkedCardsTotals.yearly.jpy,
                        linkedCardsTotals.yearly.usd,
                        usdToJpyRate,
                      )}
                    </p>
                  </div>
                </div>
              </div>
              <Accordion type="multiple" className="-mx-1">
                {linkedCards.map(({ card, activeRecurring, totals: cardTotals }) => (
                  <AccordionItem key={card.id} value={card.id} className="border-b last:border-b-0">
                    <AccordionTrigger className="px-1 py-2.5 hover:no-underline">
                      <div className="flex flex-1 items-center justify-between gap-2 pr-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span
                            className="size-2 shrink-0 rounded-full"
                            style={{ backgroundColor: card.color ?? '#475569' }}
                          />
                          <span className="truncate text-sm font-medium">{card.name}</span>
                          {card.brand && (
                            <span className="shrink-0 text-[10px] text-muted-foreground">
                              {card.brand}
                            </span>
                          )}
                        </div>
                        <div className="shrink-0 text-right">
                          <p className="text-sm font-medium tabular-nums">
                            月 {formatJpyWithUsd(cardTotals.monthly.jpy, cardTotals.monthly.usd, usdToJpyRate)}
                          </p>
                          <p className="text-[10px] text-muted-foreground tabular-nums">
                            年 {formatJpyWithUsd(cardTotals.yearly.jpy, cardTotals.yearly.usd, usdToJpyRate)}
                          </p>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-1 pb-2">
                      {activeRecurring.length === 0 ? (
                        <p className="px-2 py-3 text-center text-xs text-muted-foreground">
                          このカードに紐付く固定支出はありません
                        </p>
                      ) : (
                        <div className="divide-y">
                          {activeRecurring.map((r) => (
                            <RecurringRow key={r.id} item={r} />
                          ))}
                        </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
